import React, { useState, useEffect } from 'react';
import { Typography, Button, Divider, Box, IconButton } from '@mui/material';
import { Facebook, Twitter, Pinterest, Email } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getParquetPath } from '../utils/storeUtils';

// Helper function to format price
const formatPrice = (price) => {
  const numPrice = Number(price);
  return isNaN(numPrice) ? price : numPrice.toFixed(2);
};

const ProductInfo = ({ product }) => {
  // State for storing the additional product data
  const [additionalData, setAdditionalData] = useState({
    description: product.description || '',
    url: product.url || '',
    tracking_url: product.tracking_url || ''
  });

  useEffect(() => {
    // Only fetch if we have a product ID
    if (product.id) {
      fetchAdditionalData(product.id);
    }
  }, [product.id]);

  // Function to fetch additional product data from the Lambda API
  const fetchAdditionalData = async (productId) => {
    try {
      const parquetPath = getParquetPath();
      const apiUrl = `https://yhwecn47n7s6zz77csoqqjvvsm0twsan.lambda-url.us-east-1.on.aws/?id=${productId}&fields=description,url,tracking_url&parquetPath=${parquetPath}`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        // Silently fail and use existing data
        return;
      }
      
      const data = await response.json();
      
      setAdditionalData({
        description: data.description || product.description || '',
        url: data.url || product.url || '',
        tracking_url: data.tracking_url || product.tracking_url || ''
      });
    } catch (error) {
      // Silently ignore errors
      console.error('Error fetching additional product data:', error);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        <Link 
          to={`/search?brand=${encodeURIComponent(product.brand)}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {product.brand}
        </Link>
      </Typography>
      <Typography variant="h4" component="h1" gutterBottom>{product.name}</Typography>
      <Typography variant="h5" gutterBottom>${formatPrice(product.price)}</Typography>
      <Divider sx={{ my: 4 }} />
      <Button 
        variant="contained" 
        color="primary" 
        fullWidth
        sx={{ 
          mb: 2,
          borderRadius: '50px', // This makes it pill-shaped
          padding: '12px 0', // Increase vertical padding for a taller button
        }}
        // Use tracking_url if available, otherwise use url
        onClick={() => {
          const urlToOpen = additionalData.tracking_url || additionalData.url;
          if (urlToOpen) {
            window.open(urlToOpen, '_blank');
          }
        }}
        disabled={!(additionalData.tracking_url || additionalData.url)}
      >
        View product
      </Button>
      
      <Typography 
        variant="body2" 
        paragraph 
        sx={{ 
          my: 3,
          lineHeight: 1.8 // Increased line spacing
        }}
      >
        {additionalData.description || product.description}
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Share:</Typography>
        <IconButton aria-label="share on facebook"><Facebook /></IconButton>
        <IconButton aria-label="share on twitter"><Twitter /></IconButton>
        <IconButton aria-label="share on pinterest"><Pinterest /></IconButton>
        <IconButton aria-label="share via email"><Email /></IconButton>
      </Box>
    </Box>
  );
};

export default ProductInfo;